/**
 * Blinkit-theme "Categories" screen (``/categories``): one section per vertical that has sub-categories (its child
 * verticals and category groups), then a grid of every vertical. Every tile opens the category browser
 * (BkCatalogView). Data: ``GET /business-categories`` + ``GET /category-groups`` (cached, see lib/catalogData).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, LayoutGrid, Package, Search, X } from "lucide-react";
import { useCategoryGroups, useVerticals } from "../../../lib/catalogData";
import BizCategoryIcon from "../../../components/BizCategoryIcon";
import { BkEmptyState, BkErrorState, BkPageHeader } from "../../../components/blinkit/BkUI";

const GRID = "grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 2xl:grid-cols-10 gap-x-2.5 gap-y-4";

function Tile({ to, label, image, icon, testid }) {
    return (
        <Link to={to} data-testid={testid} className="flex flex-col items-center gap-1.5 text-center min-w-0 active:scale-95 transition">
            <span className="w-full aspect-square rounded-2xl bg-primary/[0.07] overflow-hidden flex items-center justify-center text-primary">
                {image ? <img src={image} alt="" loading="lazy" className="w-full h-full object-cover" /> : icon}
            </span>
            <span className="text-[11.5px] sm:text-[12px] font-semibold leading-tight line-clamp-2 break-words max-w-full">{label}</span>
        </Link>
    );
}

export default function BkCategoriesView() {
    const verticalsQ = useVerticals();
    const groupsQ = useCategoryGroups(undefined);
    const [query, setQuery] = useState("");
    const q = query.trim().toLowerCase();
    const has = (s) => !q || (s || "").toLowerCase().includes(q);

    const { sections, parents } = useMemo(() => {
        const all = verticalsQ.data || [];
        const groups = groupsQ.data || [];
        const ps = all.filter((v) => !v.parent_id);
        const secs = ps.map((v) => ({
            vertical: v,
            children: all.filter((c) => c.parent_id === v.id),
            groups: groups.filter((g) => g.business_category_id === v.id),
        })).filter((s) => s.children.length || s.groups.length);
        const loose = groups.filter((g) => !g.business_category_id);
        if (loose.length) secs.push({ vertical: null, children: [], groups: loose });
        return { sections: secs, parents: ps };
    }, [verticalsQ.data, groupsQ.data]);

    const loading = verticalsQ.data === null || groupsQ.data === null;
    const failed = verticalsQ.error && !(verticalsQ.data || []).length;

    const shownSections = sections.map((s) => {
        const vHit = s.vertical && has(s.vertical.name);
        return {
            ...s,
            children: vHit ? s.children : s.children.filter((c) => has(c.name)),
            groups: vHit ? s.groups : s.groups.filter((g) => has(g.name)),
        };
    }).filter((s) => s.children.length || s.groups.length);
    const shownParents = parents.filter((p) => has(p.name));

    return (
        <div className="pb-40 min-w-0" data-testid="all-categories-page" data-variant="blinkit">
            <BkPageHeader title="Categories" testid="bk-categories-header"
                          right={<Link to="/search" aria-label="Search products" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted"><Search size={20} /></Link>}>
                <div className="px-4 pb-3">
                    <label className="h-11 flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3 focus-within:border-primary focus-within:bg-card transition">
                        <Search size={17} className="text-muted-foreground shrink-0" />
                        <input data-testid="cats-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a category"
                               aria-label="Find a category" className="flex-1 min-w-0 bg-transparent outline-none text-[14px]" />
                        {query && (
                            <button type="button" onClick={() => setQuery("")} aria-label="Clear" className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
                                <X size={15} />
                            </button>
                        )}
                    </label>
                </div>
            </BkPageHeader>

            {failed ? (
                <BkErrorState onRetry={() => window.location.reload()} />
            ) : loading ? (
                <div className="px-4 pt-5" aria-hidden="true">
                    <div className="h-4 w-40 rounded bg-muted animate-pulse mb-4" />
                    <div className={GRID}>
                        {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square rounded-2xl bg-muted animate-pulse" />)}
                    </div>
                </div>
            ) : shownSections.length === 0 && shownParents.length === 0 ? (
                <BkEmptyState icon={LayoutGrid} testid="bk-cats-empty" title={q ? `No categories match “${query}”` : "No categories yet"}
                              body={q ? "Try a different word, or search all products instead." : "Categories appear here once the store sets them up."}
                              action={q && <Link to={`/search?q=${encodeURIComponent(query)}`} className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">Search products</Link>} />
            ) : (
                <div className="px-4 pt-5 space-y-8">
                    {shownSections.map((s) => {
                        const v = s.vertical;
                        const key = v ? v.slug : "more";
                        return (
                            <section key={key} data-testid={`cats-section-${key}`}>
                                <div className="flex items-center justify-between gap-3 mb-3">
                                    <h2 className="text-[17px] font-extrabold tracking-tight inline-flex items-center gap-2 min-w-0">
                                        {v && <BizCategoryIcon name={v.icon} size={19} className="text-primary shrink-0" />}
                                        <span className="truncate">{v ? v.name : "More to explore"}</span>
                                    </h2>
                                    {v && (
                                        <Link to={`/c/${v.slug}/products`} className="text-[12.5px] font-bold text-primary inline-flex items-center shrink-0" data-testid={`cats-see-all-${v.slug}`}>
                                            See all <ChevronRight size={14} />
                                        </Link>
                                    )}
                                </div>
                                <div className={GRID}>
                                    {s.children.map((c) => (
                                        <Tile key={c.id} to={`/c/${c.slug}/products`} label={c.name} image={c.image_url || c.icon_url}
                                              icon={<BizCategoryIcon name={c.icon} size={26} />} testid={`cats-sub-${c.slug}`} />
                                    ))}
                                    {s.groups.map((g) => (
                                        <Tile key={g.id} to={v ? `/c/${v.slug}/products?group=${g.slug}` : `/g/${g.slug}`} label={g.name} image={g.image_url}
                                              icon={<Package size={24} />} testid={`cats-group-${g.slug}`} />
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                    {shownParents.length > 0 && (
                        <section data-testid="cats-all">
                            <h2 className="text-[17px] font-extrabold tracking-tight mb-3">All categories</h2>
                            <div className={GRID}>
                                {shownParents.map((p) => (
                                    <Tile key={p.id} to={`/c/${p.slug}/products`} label={p.name} image={p.image_url || p.icon_url}
                                          icon={<BizCategoryIcon name={p.icon} size={26} />} testid={`cats-chip-${p.slug}`} />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
}
